import {
  getObject,
  getLeoUuid,
  LeoUUID,
  getInt32,
  LeoRPCRequest,
  LeoRPCResponse,
  LeoRPCError,
  LeoRPCResult,
  LeoRPC
} from "@surya-digital/leo-ts-types";
import { RespondentSelection } from "../types/respondentSelection";
/* eslint-disable import/export */
export abstract class AssignSurveyRPC implements
  LeoRPC<AssignSurveyRPC.Request, AssignSurveyRPC.Response, AssignSurveyRPC.Errors.Errors>
{
  abstract execute(
    request: AssignSurveyRPC.Request
  ): Promise<LeoRPCResult<AssignSurveyRPC.Response, AssignSurveyRPC.Errors.Errors>>;
}

export namespace AssignSurveyRPC {

  export class InvalidRequestError extends Error {
    constructor(message?: string) {
      super(message);
      Object.setPrototypeOf(this, new.target.prototype);
      this.name = "InvalidRequestError";
    }
  }

  export interface RequestDTO {
    respondentSelection: RespondentSelection.RespondentSelectionDTO;
    surveyId: string;
    projectId: string;
  }
  
  export class Request implements LeoRPCRequest {
    readonly isAssignSurveyRPCRequest: boolean = true;
  
    readonly respondentSelection: RespondentSelection.RespondentSelection;
    readonly surveyId: LeoUUID;
    readonly projectId: LeoUUID;
  
  
    constructor(
    respondentSelection: RespondentSelection.RespondentSelection, 
    surveyId: LeoUUID, 
    projectId: LeoUUID
    ) {
      this.respondentSelection = respondentSelection;
      this.surveyId = surveyId;
      this.projectId = projectId;
    }
  
    static fromDTO(dto: RequestDTO): Request {
      const respondentSelection = RespondentSelection.fromDTO(getObject(dto, "respondentSelection", InvalidRequestRespondentSelectionError));
      const surveyId = getLeoUuid(dto, "surveyId", InvalidRequestSurveyIdError);
      const projectId = getLeoUuid(dto, "projectId", InvalidRequestProjectIdError);
      return new Request(
        respondentSelection,
        surveyId,
        projectId
      );
    }
  
    toDTO(): RequestDTO {
      const dto: RequestDTO = {
          respondentSelection: this.respondentSelection.toDTO(),
          surveyId: this.surveyId.toDTO(),
          projectId: this.projectId.toDTO()
      };
      return dto;
    }
    }
  
  Request.prototype.toString = function toString(): string {
    return `AssignSurveyRPC(respondentSelection='${this.respondentSelection}',surveyId='${this.surveyId}',projectId='${this.projectId}')`;
  };
  

  export class InvalidResponseError extends Error {
    constructor(message?: string) {
      super(message);
      Object.setPrototypeOf(this, new.target.prototype);
      this.name = "InvalidResponseError";
    }
  }

  export interface ResponseDTO {}

  export class Response implements LeoRPCResponse {
    static fromDTO(): Response {
      return new Response();
    }

    toDTO(): ResponseDTO {
      return {}
    }
  }

  Response.prototype.toString = function toString(): string {
    return `AssignSurveyRPC.Response()`;
  };

  export enum RPCError {
    InvalidSurveyId = "INVALID_SURVEY_ID",
    InvalidProjectId = "INVALID_PROJECT_ID",
    SurveyIsClosed = "SURVEY_IS_CLOSED",
    ProjectAlreadyArchived = "PROJECT_ALREADY_ARCHIVED",
    DeletedColumnFilter = "DELETED_COLUMN_FILTER",
    RespondentsAlreadyAssigned = "RESPONDENTS_ALREADY_ASSIGNED",
    RespondentAssignmentInProgress = "RESPONDENT_ASSIGNMENT_IN_PROGRESS",
    InvalidRespondentIdFound = "INVALID_RESPONDENT_ID_FOUND",
    NoSurveyQuestionsPresent = "NO_SURVEY_QUESTIONS_PRESENT"
  }

  export namespace Errors {
    
    export interface InvalidSurveyIdDTO {
      code: string;
    }
    
    export class InvalidSurveyId extends LeoRPCError<RPCError> {
      readonly isInvalidSurveyId: boolean = true;
    
    
      constructor(
      ) {
        const code = RPCError.InvalidSurveyId;
        super(code);
      }
    
    
      toDTO(): InvalidSurveyIdDTO {
        const dto: InvalidSurveyIdDTO = {
          code: this.code,
        };
        return dto;
      }
    
      }
    
    InvalidSurveyId.prototype.toString = function toString(): string {
      return `AssignSurveyRPC.INVALID_SURVEY_ID()`;
    };
    
    
    export interface InvalidProjectIdDTO {
      code: string;
    }
    
    export class InvalidProjectId extends LeoRPCError<RPCError> {
      readonly isInvalidProjectId: boolean = true;
    
    
      constructor(
      ) {
        const code = RPCError.InvalidProjectId;
        super(code);
      }
    
    
      toDTO(): InvalidProjectIdDTO {
        const dto: InvalidProjectIdDTO = {
          code: this.code,
        };
        return dto;
      }
    
      }
    
    InvalidProjectId.prototype.toString = function toString(): string {
      return `AssignSurveyRPC.INVALID_PROJECT_ID()`;
    };
    
    
    export interface SurveyIsClosedDTO {
      code: string;
    }
    
    export class SurveyIsClosed extends LeoRPCError<RPCError> {
      readonly isSurveyIsClosed: boolean = true;
    
    
      constructor(
      ) {
        const code = RPCError.SurveyIsClosed;
        super(code);
      }
    
    
      toDTO(): SurveyIsClosedDTO {
        const dto: SurveyIsClosedDTO = {
          code: this.code,
        };
        return dto;
      }
    
      }
    
    SurveyIsClosed.prototype.toString = function toString(): string {
      return `AssignSurveyRPC.SURVEY_IS_CLOSED()`;
    };
    
    
    export interface ProjectAlreadyArchivedDTO {
      code: string;
    }
    
    export class ProjectAlreadyArchived extends LeoRPCError<RPCError> {
      readonly isProjectAlreadyArchived: boolean = true;
    
    
      constructor(
      ) {
        const code = RPCError.ProjectAlreadyArchived;
        super(code);
      }
    
    
      toDTO(): ProjectAlreadyArchivedDTO {
        const dto: ProjectAlreadyArchivedDTO = {
          code: this.code,
        };
        return dto;
      }
    
      }
    
    ProjectAlreadyArchived.prototype.toString = function toString(): string {
      return `AssignSurveyRPC.PROJECT_ALREADY_ARCHIVED()`;
    };
    
    
    export interface DeletedColumnFilterDTO {
      code: string;
    }
    
    export class DeletedColumnFilter extends LeoRPCError<RPCError> {
      readonly isDeletedColumnFilter: boolean = true;
    
    
      constructor(
      ) {
        const code = RPCError.DeletedColumnFilter;
        super(code);
      }
    
    
      toDTO(): DeletedColumnFilterDTO {
        const dto: DeletedColumnFilterDTO = {
          code: this.code,
        };
        return dto;
      }
    
      }
    
    DeletedColumnFilter.prototype.toString = function toString(): string {
      return `AssignSurveyRPC.DELETED_COLUMN_FILTER()`;
    };
    
    
    export interface RespondentsAlreadyAssignedDTO {
      code: string;
      respondentsSelected: number;
      respondentsAssigned: number;
      respondentsNotAssigned: number;
    }
    
    export class RespondentsAlreadyAssigned extends LeoRPCError<RPCError> {
      readonly isRespondentsAlreadyAssigned: boolean = true;
    
      readonly respondentsSelected: number;
      readonly respondentsAssigned: number;
      readonly respondentsNotAssigned: number;
    
      constructor(
      respondentsSelected: number, 
      respondentsAssigned: number, 
      respondentsNotAssigned: number
      ) {
        const code = RPCError.RespondentsAlreadyAssigned;
        super(code);
        this.respondentsSelected = respondentsSelected;
        this.respondentsAssigned = respondentsAssigned;
        this.respondentsNotAssigned = respondentsNotAssigned;
      }
    
      static fromDTO(dto: RespondentsAlreadyAssignedDTO): RespondentsAlreadyAssigned {
        const respondentsSelected = getInt32(dto, "respondentsSelected", InvalidResponseRespondentsAlreadyAssignedRespondentsSelectedError);
        const respondentsAssigned = getInt32(dto, "respondentsAssigned", InvalidResponseRespondentsAlreadyAssignedRespondentsAssignedError);
        const respondentsNotAssigned = getInt32(dto, "respondentsNotAssigned", InvalidResponseRespondentsAlreadyAssignedRespondentsNotAssignedError);
        return new RespondentsAlreadyAssigned(
          respondentsSelected,
          respondentsAssigned,
          respondentsNotAssigned
        );
      }
    
      toDTO(): RespondentsAlreadyAssignedDTO {
        const dto: RespondentsAlreadyAssignedDTO = {
          code: this.code,
          respondentsSelected: this.respondentsSelected,
          respondentsAssigned: this.respondentsAssigned,
          respondentsNotAssigned: this.respondentsNotAssigned
        };
        return dto;
      }
    
      }
    
    RespondentsAlreadyAssigned.prototype.toString = function toString(): string {
      return `AssignSurveyRPC.RESPONDENTS_ALREADY_ASSIGNED(respondentsSelected='${this.respondentsSelected}',respondentsAssigned='${this.respondentsAssigned}',respondentsNotAssigned='${this.respondentsNotAssigned}')`;
    };
    
    
    export interface RespondentAssignmentInProgressDTO {
      code: string;
    }
    
    export class RespondentAssignmentInProgress extends LeoRPCError<RPCError> {
      readonly isRespondentAssignmentInProgress: boolean = true;
    
    
      constructor(
      ) {
        const code = RPCError.RespondentAssignmentInProgress;
        super(code);
      }
    
    
      toDTO(): RespondentAssignmentInProgressDTO {
        const dto: RespondentAssignmentInProgressDTO = {
          code: this.code,
        };
        return dto;
      }
    
      }
    
    RespondentAssignmentInProgress.prototype.toString = function toString(): string {
      return `AssignSurveyRPC.RESPONDENT_ASSIGNMENT_IN_PROGRESS()`;
    };
    
    
    export interface InvalidRespondentIdFoundDTO {
      code: string;
    }
    
    export class InvalidRespondentIdFound extends LeoRPCError<RPCError> {
      readonly isInvalidRespondentIdFound: boolean = true;
    
    
      constructor(
      ) {
        const code = RPCError.InvalidRespondentIdFound;
        super(code);
      }
    
    
      toDTO(): InvalidRespondentIdFoundDTO {
        const dto: InvalidRespondentIdFoundDTO = {
          code: this.code,
        };
        return dto;
      }
    
      }
    
    InvalidRespondentIdFound.prototype.toString = function toString(): string {
      return `AssignSurveyRPC.INVALID_RESPONDENT_ID_FOUND()`;
    };
    
    
    export interface NoSurveyQuestionsPresentDTO {
      code: string;
    }
    
    export class NoSurveyQuestionsPresent extends LeoRPCError<RPCError> {
      readonly isNoSurveyQuestionsPresent: boolean = true;
    
    
      constructor(
      ) {
        const code = RPCError.NoSurveyQuestionsPresent;
        super(code);
      }
    
    
      toDTO(): NoSurveyQuestionsPresentDTO {
        const dto: NoSurveyQuestionsPresentDTO = {
          code: this.code,
        };
        return dto;
      }
    
      }
    
    NoSurveyQuestionsPresent.prototype.toString = function toString(): string {
      return `AssignSurveyRPC.NO_SURVEY_QUESTIONS_PRESENT()`;
    };
    

    export type Errors = InvalidSurveyId | InvalidProjectId | SurveyIsClosed | ProjectAlreadyArchived | DeletedColumnFilter | RespondentsAlreadyAssigned | RespondentAssignmentInProgress | InvalidRespondentIdFound | NoSurveyQuestionsPresent;
    export type ErrorsDTO = InvalidSurveyIdDTO | InvalidProjectIdDTO | SurveyIsClosedDTO | ProjectAlreadyArchivedDTO | DeletedColumnFilterDTO | RespondentsAlreadyAssignedDTO | RespondentAssignmentInProgressDTO | InvalidRespondentIdFoundDTO | NoSurveyQuestionsPresentDTO;

    export const fromDTO = (dto: ErrorsDTO): Errors => {
      switch (dto.code) {
        case RPCError.InvalidSurveyId:
          return new InvalidSurveyId();
        case RPCError.InvalidProjectId:
          return new InvalidProjectId();
        case RPCError.SurveyIsClosed:
          return new SurveyIsClosed();
        case RPCError.ProjectAlreadyArchived:
          return new ProjectAlreadyArchived();
        case RPCError.DeletedColumnFilter:
          return new DeletedColumnFilter();
        case RPCError.RespondentsAlreadyAssigned:
          return RespondentsAlreadyAssigned.fromDTO(dto as RespondentsAlreadyAssignedDTO);
        case RPCError.RespondentAssignmentInProgress:
          return new RespondentAssignmentInProgress();
        case RPCError.InvalidRespondentIdFound:
          return new InvalidRespondentIdFound();
        case RPCError.NoSurveyQuestionsPresent:
          return new NoSurveyQuestionsPresent();
        default:
          throw new InvalidResponseError(`Unexpected error code: ${dto.code}`);
      }
    };
  }
}

export class InvalidRequestRespondentSelectionError extends AssignSurveyRPC.InvalidRequestError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestRespondentSelectionError";
  }
}

export class InvalidRequestSurveyIdError extends AssignSurveyRPC.InvalidRequestError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestSurveyIdError";
  }
}

export class InvalidRequestProjectIdError extends AssignSurveyRPC.InvalidRequestError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestProjectIdError";
  }
}


export class InvalidResponseInvalidSurveyIdError extends AssignSurveyRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseInvalidSurveyIdError";
  }
}

export class InvalidResponseInvalidProjectIdError extends AssignSurveyRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseInvalidProjectIdError";
  }
}

export class InvalidResponseSurveyIsClosedError extends AssignSurveyRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseSurveyIsClosedError";
  }
}

export class InvalidResponseProjectAlreadyArchivedError extends AssignSurveyRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseProjectAlreadyArchivedError";
  }
}

export class InvalidResponseDeletedColumnFilterError extends AssignSurveyRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseDeletedColumnFilterError";
  }
}

export class InvalidResponseRespondentsAlreadyAssignedError extends AssignSurveyRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseRespondentsAlreadyAssignedError";
  }
}

export class InvalidResponseRespondentsAlreadyAssignedRespondentsSelectedError extends InvalidResponseRespondentsAlreadyAssignedError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseRespondentsAlreadyAssignedRespondentsSelectedError";
  }
}

export class InvalidResponseRespondentsAlreadyAssignedRespondentsAssignedError extends InvalidResponseRespondentsAlreadyAssignedError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseRespondentsAlreadyAssignedRespondentsAssignedError";
  }
}

export class InvalidResponseRespondentsAlreadyAssignedRespondentsNotAssignedError extends InvalidResponseRespondentsAlreadyAssignedError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseRespondentsAlreadyAssignedRespondentsNotAssignedError";
  }
}
export class InvalidResponseRespondentAssignmentInProgressError extends AssignSurveyRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseRespondentAssignmentInProgressError";
  }
}

export class InvalidResponseInvalidRespondentIdFoundError extends AssignSurveyRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseInvalidRespondentIdFoundError";
  }
}

export class InvalidResponseNoSurveyQuestionsPresentError extends AssignSurveyRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseNoSurveyQuestionsPresentError";
  }
}

